import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type SupportTicketType = 'bug' | 'feedback' | 'feature';
export type SupportTicketStatus = 'open' | 'in_progress' | 'planned' | 'completed' | 'closed';
export type SupportTicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface SupportTicketContext {
  analyticsSessionId?: string;
  environment?: string;
  appVersion?: string;
}

export interface SupportTicket {
  publicId: string;
  type: SupportTicketType;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  title: string;
  context: SupportTicketContext | null;
  lastActivityAt: string;
  lastUserMessageAt: string | null;
  lastOwnerMessageAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  requester: {
    publicId: string;
    name: string;
    email: string;
  };
  messageCount: number;
}

export interface SupportTicketMessage {
  publicId: string;
  authorRole: 'user' | 'owner';
  body: string;
  isInternal: boolean;
  createdAt: string;
  authorName: string;
}

export interface SupportTicketAttachment {
  publicId: string;
  fileName: string;
  mimeType: 'image/webp';
  sizeBytes: number;
  createdAt: string;
}

export interface SupportTicketDetail {
  ticket: SupportTicket;
  messages: SupportTicketMessage[];
  attachments: SupportTicketAttachment[];
}

export interface SupportApiError {
  code: number;
  message: string;
}

interface TicketListResponse {
  success: boolean;
  tickets: SupportTicket[];
}

interface TicketDetailResponse {
  success: boolean;
  ticket: SupportTicket;
  messages: SupportTicketMessage[];
  attachments?: SupportTicketAttachment[];
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class SupportService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  listMyTickets(): Observable<SupportTicket[]> {
    return this.http
      .get<TicketListResponse>(`${this.apiUrl}/support/tickets`)
      .pipe(map((response) => response.tickets), catchError(this.handleError));
  }

  createTicket(payload: {
    type: SupportTicketType;
    title: string;
    message: string;
    context?: SupportTicketContext | null;
  }): Observable<SupportTicket> {
    return this.http
      .post<TicketDetailResponse>(`${this.apiUrl}/support/tickets`, payload)
      .pipe(map((response) => response.ticket), catchError(this.handleError));
  }

  getMyTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.http
      .get<TicketDetailResponse>(`${this.apiUrl}/support/tickets/${ticketId}`)
      .pipe(map((response) => this.toTicketDetail(response)), catchError(this.handleError));
  }

  replyToMyTicket(ticketId: string, message: string): Observable<SupportTicketDetail> {
    return this.http
      .post<TicketDetailResponse>(`${this.apiUrl}/support/tickets/${ticketId}/messages`, { message })
      .pipe(map((response) => this.toTicketDetail(response)), catchError(this.handleError));
  }

  uploadMyTicketAttachment(ticketId: string, file: File): Observable<SupportTicketAttachment> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http
      .post<{ success: boolean; attachment: SupportTicketAttachment }>(`${this.apiUrl}/support/tickets/${ticketId}/attachments`, body)
      .pipe(map((response) => response.attachment), catchError(this.handleError));
  }

  getMyTicketAttachment(ticketId: string, attachmentId: string): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/support/tickets/${ticketId}/attachments/${attachmentId}`, { responseType: 'blob' })
      .pipe(catchError(this.handleError));
  }

  listOwnerTickets(): Observable<SupportTicket[]> {
    return this.http
      .get<TicketListResponse>(`${this.apiUrl}/admin/tickets`)
      .pipe(map((response) => response.tickets), catchError(this.handleError));
  }

  getOwnerTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.http
      .get<TicketDetailResponse>(`${this.apiUrl}/admin/tickets/${ticketId}`)
      .pipe(map((response) => this.toTicketDetail(response)), catchError(this.handleError));
  }

  updateOwnerTicket(
    ticketId: string,
    payload: { status?: SupportTicketStatus; priority?: SupportTicketPriority }
  ): Observable<SupportTicket> {
    return this.http
      .patch<TicketDetailResponse>(`${this.apiUrl}/admin/tickets/${ticketId}`, payload)
      .pipe(map((response) => response.ticket), catchError(this.handleError));
  }

  addOwnerMessage(ticketId: string, message: string, isInternal: boolean): Observable<SupportTicketDetail> {
    return this.http
      .post<TicketDetailResponse>(`${this.apiUrl}/admin/tickets/${ticketId}/messages`, { message, isInternal })
      .pipe(map((response) => this.toTicketDetail(response)), catchError(this.handleError));
  }

  getOwnerTicketAttachment(ticketId: string, attachmentId: string): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/admin/tickets/${ticketId}/attachments/${attachmentId}`, { responseType: 'blob' })
      .pipe(catchError(this.handleError));
  }

  private toTicketDetail(response: TicketDetailResponse): SupportTicketDetail {
    return {
      ticket: response.ticket,
      messages: response.messages,
      attachments: response.attachments ?? [],
    };
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    const message = error.error?.error?.message || error.error?.message || error.message || 'An unexpected error occurred.';
    return throwError(() => ({ code: error.status, message } as SupportApiError));
  };
}
